import { motion } from "motion/react";

import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import dayjs from "dayjs";
import isEmpty from "lodash/isEmpty";
import React, { useEffect, useState } from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

import { BsSunrise, BsSunset } from "react-icons/bs";
import { CiSun } from "react-icons/ci";
import { FaRegCircleQuestion } from "react-icons/fa6";
import GuideAutocompleteInput from "../components/guideAutocomplete";
import PillInfo from "../components/pillInfo";
import {
  Doctor,
  Guide,
  Patient,
  SaveHistory,
  SaveHistoryRequest,
  TimeToTakePills,
} from "../types";
import { getPartOfTheDayByTime, PARTS_OF_THE_DAY } from "../utils";
import { GoMoon } from "react-icons/go";
import { MdOutlineSos } from "react-icons/md";

const patientVariants = {
  hidden: { opacity: 0, x: -100 }, // Start off-screen to the left
  visible: { opacity: 1, x: 0 }, // Fade in and slide to position
  exit: { opacity: 0, x: 100 }, // Slide out to the right
};

const iconByPartOfTheDay = {
  morning: <BsSunrise />,
  noon: <CiSun />,
  evening: <BsSunset />,
  night: <GoMoon />,
  sos: <MdOutlineSos />,
};

const help = [
  {
    id: "1",
    title: "בחירת מלווה מאשר",
    content: "לחץ על תיבת הבחירה ובחר את המלווה המאשר מהרשימה",
  },
  {
    id: "2",
    title: "בחירת זמן",
    content:
      "לחץ על אחד מהכפתורים: בוקר, צהריים, ערב או לילה, בהתאם לזמן שבו תרצה לקחת את התרופות",
  },
  {
    id: "3",
    title: "בחירת כדורים",
    content:
      'לחץ על הריבוע ליד כל כדור שברצונך לקחת. אם ברצונך לבחור את כל הכדורים, לחץ על כפתור "בחר את כל הכדורים"',
  },
  {
    id: "4",
    title: "אישור",
    content: 'לאחר שבחרת את כל הכדורים, לחץ על כפתור "אישור" בתחתית העמוד',
  },
];

const PillModal = ({
  isPending,
  isShortcut = false,
  patient,
  doctors,
  onClose,
  onSave,
  onNext,
  onPrev,
}: {
  isPending?: boolean;
  isShortcut?: boolean;
  patient: Patient;
  doctors: Doctor[] | undefined;
  onClose: () => void;
  onSave: (body: {
    data: SaveHistoryRequest["pills"];
    patientId: string;
    guideId: string;
  }) => void;
  onNext?: () => void;
  onPrev?: () => void;
}) => {
  const [selectedPartOfDay, setSelectedPartOfDay] = useState<TimeToTakePills>(
    getPartOfTheDayByTime()
  );

  const [selectedGuide, setSelectedGuide] = useState<Guide | undefined>();
  const [selectedGuideError, setSelectedGuideError] = useState<boolean>(false);
  const [checkedError, setCheckedError] = useState<boolean>(false);
  const [checkedPills, setCheckedPills] = useState<SaveHistoryRequest["pills"]>(
    []
  );
  const [isDragging, setIsDragging] = useState(false);
  const [open, setOpen] = useState(false);

  const handleDragStart = ({ pillId, pillName, dosage }: SaveHistory) => {
    setIsDragging(true);
    handleCheckboxChange({ pillId, pillName, dosage });
  };

  const handleDragEnter = ({ pillId, pillName, dosage }: SaveHistory) => {
    if (isDragging) {
      handleCheckboxChange({ pillId, pillName, dosage });
    }
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    // Add 'scroll-lock' class to <body> when the modal is open
    document.body.classList.add("scroll-lock");

    // Cleanup: Remove 'scroll-lock' class when the modal is closed
    return () => {
      document.body.classList.remove("scroll-lock");
    };
  }, []);

  const preventDragBehavior = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
  };

  const handleCheckboxChange = ({ pillId, dosage, pillName }: SaveHistory) => {
    if (!dosage) {
      return;
    }

    setCheckedError(false);

    setCheckedPills((prevChecked) => {
      const isAlreadyChecked = prevChecked.some(
        (pill) => pill.pillId === pillId
      );

      if (isAlreadyChecked) {
        return prevChecked.filter((pill) => pill.pillId !== pillId);
      }

      return [
        ...prevChecked,
        {
          pillId,
          dosages: [dosage],
          pillName,
        },
      ];
    });
  };

  const filteredPills = patient.pills.filter((pill) => {
    const parts = Object.values(pill.dosages).filter((dosage) => {
      // @ts-ignore
      return dosage[selectedPartOfDay] !== undefined;
    });

    return parts.length !== 0;
  });

  return (
    <div
      onClick={() => onClose()}
      className="fixed flex-col inset-0 bg-gray-500 bg-opacity-50 flex justify-center items-center z-50">
      <motion.div
        key={patient._id}
        variants={patientVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        transition={{ duration: 0.5 }}
        onClick={(event) => event.stopPropagation()}
        style={{ minHeight: "30%", maxHeight: "83.333333%" }}
        className="bg-white p-6 rounded shadow-lg w-11/12 md:w-2/3 flex flex-col">
        <div>
          <h2 className="text-xl font-bold mb-1 text-blue-900 flex items- gap-2">
            כדורים לבליעה{" "}
            <button
              onClick={() => {
                setOpen(true);
              }}>
              <FaRegCircleQuestion size={20} />
            </button>
          </h2>
          <h3 className="font-semibold mb-4 text-blue-900 text-lg">
            {patient.name}
            {" - רופא: "}
            {doctors?.find((d) => d._id === patient.doctorId)?.name || ""}
          </h3>
        </div>

        <div
          className={`mb-6 w-1/2 mx-auto border-2 rounded ${
            selectedGuideError && "border-red-600"
          }`}>
          <GuideAutocompleteInput
            onSelected={setSelectedGuide}
            onChange={(_) => {
              if (selectedGuideError) {
                setSelectedGuideError(false);
              }
            }}
          />
        </div>

        <div className="flex flex-wrap flex-row-reverse gap-8 mx-auto">
          {patient.timeToTakePills.map((time) => {
            return (
              <button
                onClick={() => setSelectedPartOfDay(time)}
                className={`hover:scale-105 min-w-20 shadow-md rounded border p-2 flex gap-1 items-center ${
                  selectedPartOfDay === time ? "" : " bg-white "
                } ${
                  selectedPartOfDay === time && time === "morning"
                    ? "bg-yellow-300"
                    : selectedPartOfDay === time && time === "noon"
                    ? "bg-orange-300"
                    : selectedPartOfDay === time && time === "evening"
                    ? "bg-purple-300"
                    : selectedPartOfDay === time && time === "night"
                    ? "bg-purple-600 text-white"
                    : selectedPartOfDay === time && time === "sos"
                    ? "bg-red-300"
                    : ""
                } 'text-white'
                `}
                key={time}>
                {iconByPartOfTheDay[time]}
                {PARTS_OF_THE_DAY[time]?.labelHe}
              </button>
            );
          })}
        </div>

        {!patient.timeToTakePills.includes(selectedPartOfDay) ? (
          <div
            onClick={() => onClose()}
            className="flex flex-1 justify-center items-center">
            <div className="bg-white p-6 font-bold">
              <div>אין כדורים לקחת בשעה זו</div>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex flex-col overflow-scroll mt-4">
            <p className="mt-2 font-bold">
              סה״כ כדורים לבליעה: {filteredPills.length}
            </p>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <span className="font-bold text-md">שם הכדור</span>
                    </TableCell>
                    <TableCell>
                      <span className="font-bold text-md">מינון</span>
                    </TableCell>
                    <TableCell>
                      <span className="font-bold text-md">תאריך סיום</span>
                    </TableCell>
                    <TableCell>
                      <span className="font-bold text-md">נלקח</span>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredPills.map((pill) => {
                    const dosage = pill.dosages.find((dosage) => {
                      // @ts-ignore
                      return dosage[selectedPartOfDay]?.value;
                    });

                    return (
                      <TableRow
                        className={`odd:bg-slate-100 draggable ${
                          isDragging &&
                          checkedPills.some((p) => p.pillId === pill._id)
                            ? "bg-blue-100"
                            : ""
                        } ${!pill.isActive ? "opacity-30 line-through" : ""}`}
                        key={pill._id}
                        onMouseDown={(e) => {
                          preventDragBehavior(e);
                          handleDragStart({
                            pillId: pill._id,
                            pillName: pill.dragHebName,
                            // @ts-ignore
                            dosage: {
                              value:
                                // @ts-ignore
                                dosage?.[selectedPartOfDay]?.value || "",
                              partOfTheDay: selectedPartOfDay,
                            },
                          });
                        }}
                        onMouseEnter={(e) => {
                          preventDragBehavior(e);
                          handleDragEnter({
                            pillId: pill._id,
                            pillName: pill.dragHebName,
                            // @ts-ignore
                            dosage: {
                              value:
                                // @ts-ignore
                                dosage?.[selectedPartOfDay]?.value || "",
                              partOfTheDay: selectedPartOfDay,
                            },
                          });
                        }}
                        onMouseUp={handleDragEnd}>
                        <TableCell>
                          {pill.notes && (
                            <PillInfo notes={pill.notes || "אין הערות"} />
                          )}
                          {pill.dragHebName}
                        </TableCell>
                        <TableCell>
                          {
                            // @ts-ignore
                            dosage?.[selectedPartOfDay]?.value || ""
                          }
                        </TableCell>
                        <TableCell>
                          {pill.dateRange.endDate
                            ? dayjs(pill.dateRange.endDate).format("DD.MM.YYYY")
                            : "--"}
                        </TableCell>
                        <TableCell>
                          <Checkbox
                            checked={checkedPills.some(
                              (p) => p.pillId === pill._id
                            )}
                            color="primary"
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        )}
      </motion.div>
      <motion.div
        key={patient._id + 1}
        variants={patientVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        transition={{ duration: 0.5 }}
        onClick={(event) => event.stopPropagation()}
        className="bg-white w-11/12 md:w-2/3 gap-2 flex flex-col px-10 pb-8 rounded">
        <button
          disabled={isPending}
          onClick={() => {
            let errorShow = false;
            if (!selectedGuide) {
              setSelectedGuideError(true);
              errorShow = true;
            }

            if (isEmpty(checkedPills)) {
              setCheckedError(true);
              errorShow = true;
            }

            if (errorShow) {
              return;
            }

            onSave({
              guideId: selectedGuide?._id || "",
              patientId: patient._id,
              data: checkedPills,
            });
            !isShortcut && onClose();
          }}
          className={`hover:scale-105 text-white rounded py-2 px-4 mt-4 w-1/2 mx-auto ${
            isPending
              ? "bg-gray-600"
              : isEmpty(checkedPills) || checkedError
              ? "bg-red-600"
              : "bg-[#144082]"
          }`}>
          {/* */}
          {isEmpty(checkedPills)
            ? "אף כדור לא מסומן, חייב לסמן אחד לפחות"
            : "הדייר לקח את הכדורים המסומנים "}
        </button>
        <button
          onClick={onClose}
          className="hover:scale-105 text-[#144082] border border-[#144082] bg-white rounded py-2 px-4 mt-4 w-1/2 mx-auto">
          ביטול
        </button>

        <div className="flex justify-between mt-4">
          {onPrev && (
            <button
              onClick={onPrev}
              className="hover:scale-105 text-white bg-[#144082] rounded py-2 px-4 flex justify-center items-center gap-2">
              <FaChevronRight />
              הדייר הקודם
            </button>
          )}
          {onNext && (
            <button
              onClick={onNext}
              className="hover:scale-105 text-white bg-[#144082] rounded py-2 px-4 flex justify-center items-center gap-2">
              הדייר הבא
              <FaChevronLeft />
            </button>
          )}
        </div>

        <Dialog open={open} onClose={() => setOpen(false)}>
          <DialogTitle>עזרה</DialogTitle>
          <DialogContent>
            {help.map((h, index) => (
              <div key={h.id} className="mb-2">
                <div className="flex gap-2 mb-1">
                  <span className="bg-red-200 text-red-800 font-bold px-2 py-1 rounded-full text-xs">
                    {index + 1}
                  </span>
                  <h3 className="font-bold">{h.title}</h3>
                </div>
                <p>{h.content}</p>
              </div>
            ))}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>סגור</Button>
          </DialogActions>
        </Dialog>
      </motion.div>
    </div>
  );
};

export default PillModal;
